import { useMemo, useState } from 'react';
import { TOPICS, buildEmail, mailtoHref, type DetailLine, type TopicKey } from './buildEmail.js';
import { sendContact } from './sendContact.js';

const BRAND_NAME = import.meta.env.VITE_BRAND_NAME ?? 'VoxFaunix';
/** Same address the API's BRAND_CONTACT_EMAIL defaults to — one address, not two to keep in sync. */
const SUPPORT_EMAIL = import.meta.env.VITE_BRAND_CONTACT_EMAIL ?? 'contact@voxfaunix.com';

/** Unset (dev without a key, or a build where the CI variable is missing) means no in-app form. */
const ACCESS_KEY = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY?.trim() || null;

const MIN_MESSAGE_LENGTH = 10;
// Deliberately loose — the browser's own type=email check does the real work,
// this only keeps the button honest about "looks like an address".
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * What a first reply usually needs and people forget to say. Read once when
 * the page opens: it is a snapshot of the moment they decided to write.
 *
 * Deliberately no email, name, token or account identifier — the message
 * comes from their own mail app, which already says who they are, and nothing
 * here should be something they would be surprised to see in an inbox.
 */
function collectDetails(): DetailLine[] {
  const installed =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;

  return [
    { label: 'Page', value: window.location.hash || '#/' },
    { label: 'Installed app', value: installed ? 'yes' : 'no (browser tab)' },
    { label: 'Browser', value: navigator.userAgent },
    { label: 'Screen', value: `${window.innerWidth} × ${window.innerHeight}` },
    { label: 'Online', value: navigator.onLine ? 'yes' : 'no' },
    { label: 'Time', value: new Date().toISOString() },
  ];
}

type Copied = 'address' | 'message' | 'failed' | null;
type SendState = 'idle' | 'sending' | 'sent' | 'network' | 'rejected';

/**
 * Contact and support.
 *
 * Helps someone write a *good* message — pick what it is about, get the
 * prompt for what a reply would need, see exactly what technical detail will
 * be attached — then sends it through Web3Forms when a key is configured.
 *
 * The email-app and copy buttons stay in both modes. With a key they are the
 * fallback when sending fails (this is a PWA: offline is ordinary); without
 * one they are the whole feature, and cover people with no mail app set up
 * (webmail, a shared tablet), who would otherwise tap "email us" and see
 * nothing happen.
 */
export function ContactPage() {
  const [topicKey, setTopicKey] = useState<TopicKey>('question');
  const [message, setMessage] = useState('');
  const [includeDetails, setIncludeDetails] = useState(true);
  const [copied, setCopied] = useState<Copied>(null);
  const [opened, setOpened] = useState(false);
  const [replyTo, setReplyTo] = useState('');
  const [botcheck, setBotcheck] = useState('');
  const [sendState, setSendState] = useState<SendState>('idle');

  const details = useMemo(collectDetails, []);
  const topic = TOPICS.find((t) => t.key === topicKey)!;
  const email = buildEmail(BRAND_NAME, topic, message, includeDetails ? details : null);
  const messageReady = message.trim().length >= MIN_MESSAGE_LENGTH;
  const emailReady = EMAIL_PATTERN.test(replyTo.trim());
  // The in-app form needs somewhere to reply; the email-app route doesn't.
  const ready = messageReady && (!ACCESS_KEY || emailReady);
  const sending = sendState === 'sending';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ACCESS_KEY || !ready || sending) return;

    // A person never sees or fills the honeypot. Pretend it worked so a bot
    // learns nothing, and send nothing.
    if (botcheck) {
      setSendState('sent');
      return;
    }

    setSendState('sending');
    const result = await sendContact({
      accessKey: ACCESS_KEY,
      email: replyTo.trim(),
      subject: email.subject,
      message: email.body,
      botcheck,
    });
    setSendState(result.ok ? 'sent' : result.reason);
  };

  const reset = () => {
    setMessage('');
    setSendState('idle');
    setCopied(null);
    setOpened(false);
  };

  const copy = async (what: 'address' | 'message') => {
    const text =
      what === 'address'
        ? SUPPORT_EMAIL
        : `To: ${SUPPORT_EMAIL}\nSubject: ${email.subject}\n\n${email.body}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
    } catch {
      setCopied('failed');
    }
  };

  if (sendState === 'sent') {
    return (
      <div>
        <p className="eyebrow">Contact</p>
        <h1 className="page-title">Message sent</h1>
        <section className="panel contact-sent" role="status">
          <p className="contact-sent-mark" aria-hidden="true">
            ✓
          </p>
          <p style={{ margin: '0 0 0.5rem' }}>
            Thank you — it is with us now. Every message is read, and we will reply to{' '}
            <strong>{replyTo.trim()}</strong>.
          </p>
          <p className="field-hint" style={{ margin: '0 0 1.25rem' }}>
            If you don't hear back, check your spam folder, or write to {SUPPORT_EMAIL}.
          </p>
          <div className="contact-actions">
            <a className="btn" href="#/contribute">
              Back to recording
            </a>
            <button type="button" className="btn btn--ghost" onClick={reset}>
              Send another
            </button>
          </div>
        </section>
      </div>
    );
  }

  const failed = sendState === 'network' || sendState === 'rejected';

  return (
    <div>
      <p className="eyebrow">Contact</p>
      <h1 className="page-title">Get in touch</h1>
      <p className="page-intro">
        Every message is read, even the short ones. Tell us what it is about and we will help you
        say the useful parts. Looking for a quick answer instead? The{' '}
        <a href="#/help" style={{ color: 'var(--context-ink)' }}>
          Help page
        </a>{' '}
        covers the common questions.
      </p>

      <form className="contact-layout" onSubmit={(e) => void submit(e)} noValidate>
        <div>
          <section className="panel">
            <p className="eyebrow" id="contact-topic-label" style={{ marginBottom: '0.9rem' }}>
              1 · What is it about?
            </p>
            <div className="contact-topics" role="radiogroup" aria-labelledby="contact-topic-label">
              {TOPICS.map((t) => (
                <label className="contact-topic" key={t.key}>
                  <input
                    type="radio"
                    name="contact-topic"
                    value={t.key}
                    checked={t.key === topicKey}
                    onChange={() => {
                      setTopicKey(t.key);
                      setOpened(false);
                    }}
                  />
                  <span className="contact-topic-card">
                    <span className="contact-topic-label">{t.label}</span>
                    <span className="contact-topic-blurb">{t.blurb}</span>
                  </span>
                </label>
              ))}
            </div>
          </section>

          <section className="panel">
            <p className="eyebrow" style={{ marginBottom: '0.9rem' }}>
              2 · Your message
            </p>
            <div className="field" style={{ marginBottom: '1rem' }}>
              <label className="field-label" htmlFor="contact-message">
                {topic.label}
              </label>
              <textarea
                id="contact-message"
                rows={6}
                value={message}
                placeholder={topic.prompt}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setOpened(false);
                }}
              />
              <p className="field-hint">{topic.prompt}</p>
            </div>

            {ACCESS_KEY && (
              <div className="field" style={{ marginBottom: '1rem' }}>
                <label className="field-label" htmlFor="contact-email">
                  Your email, so we can reply
                </label>
                <input
                  id="contact-email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  value={replyTo}
                  aria-invalid={replyTo.trim() !== '' && !emailReady}
                  onChange={(e) => setReplyTo(e.target.value)}
                />
                <p className="field-hint">
                  Used only to answer this message. It is not attached to your recordings or the
                  corpus.
                </p>
              </div>
            )}

            {/* Honeypot. Off-screen rather than display:none, since some bots skip
                hidden fields; tabIndex and aria-hidden keep people out of it. */}
            <div className="contact-honeypot" aria-hidden="true">
              <label>
                Leave this empty
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={botcheck}
                  onChange={(e) => setBotcheck(e.target.value)}
                />
              </label>
            </div>

            <label className="contact-check">
              <input
                type="checkbox"
                checked={includeDetails}
                onChange={(e) => setIncludeDetails(e.target.checked)}
              />
              <span>
                Include technical details
                <span className="field-hint" style={{ display: 'block', marginTop: '0.15rem' }}>
                  Your browser, screen size and the page you were on. It usually saves a round of
                  questions. Nothing that identifies you.
                </span>
              </span>
            </label>

            {includeDetails && (
              <details className="contact-details">
                <summary>See exactly what will be added</summary>
                <dl>
                  {details.map((d) => (
                    <div key={d.label}>
                      <dt>{d.label}</dt>
                      <dd>{d.value}</dd>
                    </div>
                  ))}
                </dl>
              </details>
            )}
          </section>

          <section className="panel">
            <p className="eyebrow" style={{ marginBottom: '0.9rem' }}>
              3 · Send it
            </p>

            {failed && (
              <div className="notice notice--error" role="alert" style={{ marginBottom: '1rem' }}>
                {sendState === 'network'
                  ? "That didn't go through — you may be offline. Your message is still here, so try again in a moment."
                  : "We couldn't send that just now. Your message is still here — try again, or use your email app below."}
              </div>
            )}

            <div className="contact-actions">
              {ACCESS_KEY ? (
                <>
                  <button type="submit" className="btn" disabled={!ready || sending}>
                    {sending ? 'Sending…' : failed ? 'Try again' : 'Send message'}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    disabled={!messageReady}
                    onClick={() => {
                      window.location.href = mailtoHref(SUPPORT_EMAIL, email);
                      setOpened(true);
                    }}
                  >
                    Use my email app instead
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn"
                    disabled={!ready}
                    onClick={() => {
                      window.location.href = mailtoHref(SUPPORT_EMAIL, email);
                      setOpened(true);
                    }}
                  >
                    Open in my email app
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    disabled={!ready}
                    onClick={() => void copy('message')}
                  >
                    Copy message
                  </button>
                </>
              )}
            </div>

            <p className="field-hint" role="status" aria-live="polite">
              {!messageReady
                ? 'Write a line or two above and this unlocks.'
                : ACCESS_KEY && !emailReady
                  ? 'Add your email above so we can reply.'
                  : copied === 'message'
                    ? `Copied. Paste it into an email to ${SUPPORT_EMAIL}.`
                    : copied === 'failed'
                      ? `Your browser blocked copying. Select the text yourself, or write to ${SUPPORT_EMAIL}.`
                      : opened
                        ? 'Your email app should have opened with the message ready to send. Nothing opened? Copy the address on the right.'
                        : ACCESS_KEY
                          ? 'Sent straight from here. Prefer your own email app? Use the other button.'
                          : 'This opens your own email app with everything filled in. Nothing is sent until you press send there.'}
            </p>
          </section>
        </div>

        <aside className="contact-aside">
          <section className="panel">
            <p className="eyebrow" style={{ marginBottom: '0.6rem' }}>
              Prefer to write it yourself?
            </p>
            <p className="contact-address">{SUPPORT_EMAIL}</p>
            <button type="button" className="btn btn--ghost" onClick={() => void copy('address')}>
              {copied === 'address' ? 'Copied ✓' : 'Copy address'}
            </button>
          </section>

          <section className="panel">
            <p className="eyebrow" style={{ marginBottom: '0.6rem' }}>
              Helpful to include
            </p>
            <ul className="contact-tips">
              <li>What you were trying to do, in one line.</li>
              <li>For a recording: its date and which animal.</li>
              <li>For sign-in trouble: the email you used, every time.</li>
            </ul>
          </section>
        </aside>
      </form>
    </div>
  );
}
